import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Card,
    CardContent,
    LinearProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Radio,
    RadioGroup,
    FormControl,
    FormControlLabel,
    FormLabel,
    Checkbox,
    FormGroup,
    TextField,
    Divider,
    Grid,
    IconButton,
    Stepper,
    Step,
    StepLabel
} from '@mui/material';
import {
    Timer as TimerIcon,
    Quiz as QuizIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Visibility as EyeIcon,
    VisibilityOff as EyeOffIcon,
    ArrowBack as BackIcon,
    ArrowForward as ForwardIcon,
    Flag as FlagIcon
} from '@mui/icons-material';
import axios from 'axios';

export default function TrainingTest() {
    const [testState, setTestState] = useState('dashboard'); // dashboard, taking, results
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [testSettings, setTestSettings] = useState({});
    const [testHistory, setTestHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [testResults, setTestResults] = useState(null);

    // Anti-cheat state
    const [focusViolations, setFocusViolations] = useState(0);
    const [tabSwitches, setTabSwitches] = useState(0);
    const [violations, setViolations] = useState([]);
    const [isWindowFocused, setIsWindowFocused] = useState(true);

    // UI state
    const [showExplanations, setShowExplanations] = useState(false);
    const [violationDialog, setViolationDialog] = useState(false);

    const timerRef = useRef(null);
    const testStartRef = useRef(null);

    useEffect(() => {
        loadDashboardData();
        setupAntiCheat();

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const [settingsRes, historyRes] = await Promise.all([
                axios.get('/api/training-test/settings'),
                axios.get('/api/training-test/history')
            ]);

            setTestSettings(settingsRes.data);
            setTestHistory(historyRes.data);
        } catch (err) {
            setError('Failed to load dashboard data: ' + err.response?.data?.error || err.message);
        }
        setLoading(false);
    };

    const setupAntiCheat = () => {
        const handleFocus = () => {
            if (!isWindowFocused && testState === 'taking') {
                setIsWindowFocused(true);
                recordViolation('focus');
            }
        };

        const handleBlur = () => {
            if (testState === 'taking') {
                setIsWindowFocused(false);
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden && testState === 'taking') {
                recordViolation('tab');
            }
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    };

    const recordViolation = async (type) => {
        try {
            const response = await axios.post('/api/training-test/violation', { type });

            if (type === 'focus') {
                setFocusViolations(response.data.focusViolations);
            } else {
                setTabSwitches(response.data.tabSwitches);
            }

            const totalViolations = response.data.total;
            const maxAllowed = parseInt(testSettings.max_violations_allowed) || 3;

            if (totalViolations >= maxAllowed) {
                setViolationDialog(true);
            } else if (totalViolations >= maxAllowed - 1) {
                setViolations(prev => [...prev, {
                    type,
                    message: `Warning: ${type === 'focus' ? 'Focus lost' : 'Tab switched'}. ${maxAllowed - totalViolations} violations remaining.`,
                    time: new Date()
                }]);
            }
        } catch (err) {
            console.error('Failed to record violation:', err);
        }
    };

    const startTest = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/training-test/start-test');
            const { questions: testQuestions, timeRemaining: time, resuming, currentIndex, settings } = response.data;

            setQuestions(testQuestions);
            setTimeRemaining(time);
            setCurrentQuestionIndex(currentIndex || 0);
            setTestSettings(settings || testSettings);
            setTestState('taking');

            if (resuming) {
                setAnswers(response.data.answers || {});
            } else {
                setAnswers({});
                setFocusViolations(0);
                setTabSwitches(0);
                setViolations([]);
            }

            // Start timer
            testStartRef.current = Date.now();
            timerRef.current = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        submitTest(true); // Auto-submit when time runs out
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

        } catch (err) {
            setError('Failed to start test: ' + err.response?.data?.error || err.message);
        }
        setLoading(false);
    };

    const saveAnswer = async (questionId, answer) => {
        const newAnswers = { ...answers, [questionId]: answer };
        setAnswers(newAnswers);

        try {
            await axios.post('/api/training-test/save-answer', {
                questionId,
                answer,
                currentIndex: currentQuestionIndex,
                timeRemaining
            });
        } catch (err) {
            console.error('Failed to save answer:', err);
        }
    };

    const submitTest = async (timeExpired = false) => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        setLoading(true);
        try {
            const response = await axios.post('/api/training-test/submit-test', {
                focusViolations,
                tabSwitches
            });

            setTestResults(response.data);
            setTestState('results');

            if (timeExpired) {
                setError('Time expired! Test submitted automatically.');
            }

        } catch (err) {
            setError('Failed to submit test: ' + err.response?.data?.error || err.message);
        }
        setLoading(false);
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const getTimerColor = () => {
        if (timeRemaining > 300) return 'success'; // > 5 minutes
        if (timeRemaining > 60) return 'warning';  // > 1 minute
        return 'error'; // < 1 minute
    };

    const renderQuestion = (question, index) => {
        const answer = answers[question.id];

        return (
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Question {index + 1} of {questions.length}
                        </Typography>
                        <Chip
                            label={question.difficulty}
                            color={
                                question.difficulty === 'easy' ? 'success' :
                                question.difficulty === 'hard' ? 'error' : 'warning'
                            }
                            size="small"
                        />
                    </Box>

                    <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
                        {question.question}
                    </Typography>

                    {question.type === 'multiple_choice' && (
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={answer || ''}
                                onChange={(e) => saveAnswer(question.id, e.target.value)}
                            >
                                {question.options.map((option, optionIndex) => (
                                    <FormControlLabel
                                        key={optionIndex}
                                        value={option}
                                        control={<Radio />}
                                        label={option}
                                        sx={{ mb: 1 }}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    )}

                    {question.type === 'true_false' && (
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={answer || ''}
                                onChange={(e) => saveAnswer(question.id, e.target.value)}
                            >
                                <FormControlLabel value="true" control={<Radio />} label="True" />
                                <FormControlLabel value="false" control={<Radio />} label="False" />
                            </RadioGroup>
                        </FormControl>
                    )}

                    {question.type === 'checkbox' && (
                        <FormGroup>
                            {question.options.map((option, optionIndex) => (
                                <FormControlLabel
                                    key={optionIndex}
                                    control={
                                        <Checkbox
                                            checked={Array.isArray(answer) ? answer.includes(option) : false}
                                            onChange={(e) => {
                                                let newAnswer = Array.isArray(answer) ? [...answer] : [];
                                                if (e.target.checked) {
                                                    newAnswer.push(option);
                                                } else {
                                                    newAnswer = newAnswer.filter(a => a !== option);
                                                }
                                                saveAnswer(question.id, newAnswer);
                                            }}
                                        />
                                    }
                                    label={option}
                                    sx={{ mb: 1 }}
                                />
                            ))}
                        </FormGroup>
                    )}

                    {question.type === 'fill_blank' && (
                        <TextField
                            fullWidth
                            placeholder="Enter your answer..."
                            value={answer || ''}
                            onChange={(e) => saveAnswer(question.id, e.target.value)}
                            sx={{ mt: 1 }}
                        />
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderDashboard = () => (
        <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>Training Test</Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                <QuizIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Start New Test
                            </Typography>

                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Test your knowledge with {testSettings.questions_per_test || 10} questions
                                from our training materials.
                            </Typography>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" color="textSecondary">
                                    • Time limit: {testSettings.time_limit_minutes || 15} minutes
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    • Pass percentage: {testSettings.pass_percentage || 70}%
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    • Questions will be randomly selected
                                </Typography>
                                {testSettings.anti_cheat_enabled === 'true' && (
                                    <Typography variant="body2" color="warning.main">
                                        • Anti-cheat monitoring is active
                                    </Typography>
                                )}
                            </Box>

                            <Button
                                variant="contained"
                                size="large"
                                onClick={startTest}
                                disabled={loading}
                                startIcon={<QuizIcon />}
                            >
                                Start Test
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Recent Attempts</Typography>

                            {testHistory.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                    No previous attempts
                                </Typography>
                            ) : (
                                testHistory.slice(0, 5).map((attempt) => (
                                    <Box key={attempt.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2">
                                                {new Date(attempt.completed_at).toLocaleDateString()}
                                            </Typography>
                                            <Chip
                                                label={`${attempt.score}%`}
                                                color={attempt.passed ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </Box>
                                        <Typography variant="caption" color="textSecondary">
                                            {Math.round(attempt.time_taken / 60)} minutes
                                            {attempt.is_flagged && (
                                                <Chip
                                                    label="Flagged"
                                                    color="warning"
                                                    size="small"
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Typography>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );

    const renderTest = () => (
        <Box>
            {/* Header with timer and progress */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TimerIcon sx={{ mr: 1 }} />
                            <Typography variant="h6" color={getTimerColor() + '.main'}>
                                {formatTime(timeRemaining)}
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 2 }}>
                                Question {currentQuestionIndex + 1} of {questions.length}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <LinearProgress
                            variant="determinate"
                            value={(currentQuestionIndex + 1) / questions.length * 100}
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                    </Grid>
                </Grid>

                {/* Anti-cheat warnings */}
                {(focusViolations > 0 || tabSwitches > 0) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FlagIcon sx={{ mr: 1 }} />
                            Violations detected: {focusViolations} focus losses, {tabSwitches} tab switches
                        </Box>
                    </Alert>
                )}
            </Paper>

            {/* Current Question */}
            {questions[currentQuestionIndex] && renderQuestion(questions[currentQuestionIndex], currentQuestionIndex)}

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                    disabled={currentQuestionIndex === 0}
                >
                    Previous
                </Button>

                {currentQuestionIndex === questions.length - 1 ? (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => submitTest()}
                        disabled={loading}
                    >
                        Submit Test
                    </Button>
                ) : (
                    <Button
                        endIcon={<ForwardIcon />}
                        onClick={() => setCurrentQuestionIndex(Math.min(questions.length - 1, currentQuestionIndex + 1))}
                        disabled={currentQuestionIndex === questions.length - 1}
                    >
                        Next
                    </Button>
                )}
            </Box>

            {/* Question overview */}
            <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Question Overview</Typography>
                <Grid container spacing={1}>
                    {questions.map((q, index) => (
                        <Grid item key={q.id}>
                            <IconButton
                                size="small"
                                onClick={() => setCurrentQuestionIndex(index)}
                                color={
                                    answers[q.id] ? 'success' :
                                    index === currentQuestionIndex ? 'primary' : 'default'
                                }
                                sx={{
                                    border: 1,
                                    borderColor: index === currentQuestionIndex ? 'primary.main' : 'grey.300'
                                }}
                            >
                                {index + 1}
                            </IconButton>
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        </Box>
    );

    const renderResults = () => (
        <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>Test Results</Typography>

            {testResults && (
                <>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Grid container spacing={3} alignItems="center">
                                <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                                    <Typography variant="h2" color={testResults.passed ? 'success.main' : 'error.main'}>
                                        {testResults.score}%
                                    </Typography>
                                    <Typography variant="h6" color={testResults.passed ? 'success.main' : 'error.main'}>
                                        {testResults.passed ? 'PASSED' : 'FAILED'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={8}>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Correct Answers:</strong> {testResults.correctCount} / {testResults.totalQuestions}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Time Taken:</strong> {Math.round(testResults.timeTaken / 60)} minutes
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Pass Required:</strong> {testResults.passPercentage}%
                                    </Typography>
                                    {testResults.isFlagged && (
                                        <Alert severity="warning" sx={{ mt: 2 }}>
                                            This attempt has been flagged for review: {testResults.flaggedReason}
                                        </Alert>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {testSettings.show_explanations === 'true' && (
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">Question Review</Typography>
                                    <Button
                                        startIcon={showExplanations ? <EyeOffIcon /> : <EyeIcon />}
                                        onClick={() => setShowExplanations(!showExplanations)}
                                    >
                                        {showExplanations ? 'Hide' : 'Show'} Explanations
                                    </Button>
                                </Box>

                                {testResults.results.map((result, index) => (
                                    <Box key={result.questionId} sx={{ mb: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ mr: 2 }}>
                                                Question {index + 1}
                                            </Typography>
                                            {result.isCorrect ? (
                                                <CheckIcon color="success" />
                                            ) : (
                                                <CancelIcon color="error" />
                                            )}
                                        </Box>

                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {result.question}
                                        </Typography>

                                        <Typography variant="body2" color={result.isCorrect ? 'success.main' : 'error.main'}>
                                            <strong>Your Answer:</strong> {
                                                Array.isArray(result.userAnswer)
                                                    ? result.userAnswer.join(', ')
                                                    : (result.userAnswer || 'No answer')
                                            }
                                        </Typography>

                                        {!result.isCorrect && (
                                            <Typography variant="body2" color="textSecondary">
                                                <strong>Correct Answer:</strong> {
                                                    Array.isArray(result.correctAnswer)
                                                        ? result.correctAnswer.join(', ')
                                                        : result.correctAnswer
                                                }
                                            </Typography>
                                        )}

                                        {showExplanations && result.explanation && (
                                            <Typography variant="body2" color="info.main" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                {result.explanation}
                                            </Typography>
                                        )}

                                        {index < testResults.results.length - 1 && <Divider sx={{ mt: 2 }} />}
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setTestState('dashboard');
                                setTestResults(null);
                                setAnswers({});
                                setCurrentQuestionIndex(0);
                                setFocusViolations(0);
                                setTabSwitches(0);
                                setViolations([]);
                                loadDashboardData();
                            }}
                        >
                            Return to Dashboard
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
            {testState === 'dashboard' && renderDashboard()}
            {testState === 'taking' && renderTest()}
            {testState === 'results' && renderResults()}

            {/* Violation Dialog */}
            <Dialog open={violationDialog} disableEscapeKeyDown>
                <DialogTitle color="error.main">Test Terminated</DialogTitle>
                <DialogContent>
                    <Typography>
                        Your test has been terminated due to too many anti-cheat violations.
                        Please contact your supervisor for further instructions.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setViolationDialog(false);
                            setTestState('dashboard');
                        }}
                        variant="contained"
                    >
                        Return to Dashboard
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}